/**
 * Decode common HTML entities in plain strings for JSON-LD (e.g. "&amp;" → "&").
 * SSR-safe; does not use DOM.
 *
 * @param {string} input
 * @returns {string}
 */
export function decodeHtmlEntities(input) {
  if (typeof input !== "string" || !input) {
    return input;
  }
  if (!input.includes("&")) {
    return input;
  }

  return input.replace(
    /&(#[xX][\dA-Fa-f]+|#\d+|[a-zA-Z][a-zA-Z0-9]*);/g,
    (full, entity) => {
      if (entity[0] === "#") {
        const cp =
          entity[1] === "x" || entity[1] === "X"
            ? parseInt(entity.slice(2), 16)
            : parseInt(entity.slice(1), 10);
        if (Number.isNaN(cp) || cp < 0) {
          return full;
        }
        try {
          return String.fromCodePoint(cp);
        } catch {
          return full;
        }
      }
      const named = {
        amp: "&",
        lt: "<",
        gt: ">",
        quot: '"',
        apos: "'",
        nbsp: " ",
      };
      const lower = entity.toLowerCase();
      return Object.prototype.hasOwnProperty.call(named, lower)
        ? named[lower]
        : full;
    }
  );
}

/**
 * Deep-clone JSON-LD–like structures and decode HTML entities in every string value.
 *
 * @param {*} value
 * @returns {*}
 */
export function sanitizeJsonLdStrings(value) {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value === "string") {
    return decodeHtmlEntities(value);
  }
  if (Array.isArray(value)) {
    return value.map(sanitizeJsonLdStrings);
  }
  if (typeof value === "object" && value.constructor === Object) {
    const out = {};
    for (const key of Object.keys(value)) {
      out[key] = sanitizeJsonLdStrings(value[key]);
    }
    return out;
  }
  return value;
}
